import { Box, Button, Paper, styled } from '@mui/material';
import { TextV2 } from '@insights-ltd/design-library/components';
import OrganisationAutocomplete from 'components/OrganisationPicker/OrganisationAutocomplete';
import { Group, OrganisationResponse } from 'api';
import { SpecialHandlingChip } from 'components/SpecialHandlingTag';
import type { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';

const StyledText = styled(TextV2)({
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  fontSize: '1rem !important',
});

type OrgListRenderProps = {
  name: string;
  actionText: string;
  onRemove: (id: string) => void;
  id: string;
  isPrivate: boolean;
};

export const OrgListRender = ({
  name,
  isPrivate,
  actionText,
  onRemove,
  id,
}: OrgListRenderProps) => {
  return (
    <Box
      sx={{
        padding: '0.75rem 1rem 0.75rem 1.25rem',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
      <StyledText component="span" color="textPrimary" variant="bodyBold">
        {name}
      </StyledText>
      {isPrivate && (
        <Box sx={{ marginInline: 1 }}>
          <SpecialHandlingChip />
        </Box>
      )}
      <div>
        <Button
          onClick={() => onRemove(id)}
          sx={{ minWidth: 0, padding: 0 }}
          color="error"
        >
          {actionText}
        </Button>
      </div>
    </Box>
  );
};

type OrgsInGroupSearchProps = {
  groupId?: string;
  onSelect: (value: OrganisationResponse) => void;
  loading: boolean;
  organisationsInGroups?: GroupOrganisationHashMap;
  labelKey: string;
  selectedMultiOrgs: OrganisationResponse[] | [];
  selectedGroup: Group | null;
  t: (v: string) => string;
  onRemoveOrg: (id: string) => void;
  showOrgList?: boolean;
  dataTestId?: string;
};

export const OrgsInGroupSearch = ({
  groupId,
  onSelect,
  labelKey,
  selectedMultiOrgs,
  selectedGroup,
  t,
  onRemoveOrg,
  showOrgList,
  dataTestId,
}: OrgsInGroupSearchProps) => {
  const onSelectHandler = (value: OrganisationResponse) => {
    if (value?.id) {
      onSelect(value);
    }
  };
  return (
    <Box
      sx={{
        marginTop: '1rem',
      }}
    >
      {selectedGroup ? (
        <Box
          sx={{
            marginTop: '1rem',
            marginBottom: '1rem',
          }}
        >
          <TextV2 component="span" variant="bodyBold">
            {t(
              'ui.event-management.users.invite.select-user-option.multi-org.label',
            )}
          </TextV2>
        </Box>
      ) : null}
      {(selectedGroup || showOrgList) &&
      selectedMultiOrgs.length &&
      selectedMultiOrgs.filter((org) => !!org?.id).length ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            '>:last-child': { borderBottom: 'none' },
          }}
        >
          {selectedMultiOrgs.map((org: OrganisationResponse) => (
            <OrgListRender
              key={org.id}
              name={org.name}
              isPrivate={org.visibility === 'PRIVATE'}
              id={org.id}
              actionText={t(
                'ui.event-management.organisations.edit.select-group.remove',
              )}
              onRemove={onRemoveOrg}
            />
          ))}
        </Paper>
      ) : null}
      <Paper
        variant="outlined"
        sx={(theme) => ({
          backgroundColor: theme.palette.grey[200],
          borderTopLeftRadius: '0',
          borderTopRightRadius: '0',
          marginTop: '-2px',
        })}
      >
        <Box
          sx={{
            paddingTop: '12px',
            paddingBottom: '24px',
            width: '100%',
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
        >
          <OrganisationAutocomplete
            groupId={groupId}
            dataTestId={dataTestId}
            excludedOrganisationIds={selectedMultiOrgs?.map((org) => org.id)}
            onSelect={onSelectHandler}
            labelKey={labelKey}
            variant="multi-select"
          />
        </Box>
      </Paper>
    </Box>
  );
};
